import type { CSSProperties, ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Brain,
  CheckCircle2,
  ClipboardList,
  FileImage,
  FileText,
  Layers3,
  Lightbulb,
  MessageSquareText,
  Paperclip,
  Play,
  Route,
  Search,
  Sparkles,
  Target,
  Timer,
  WandSparkles,
} from 'lucide-react';
import { PageContainer } from '@/interfaces/learner/ui';

type ConceptId = 'canvas' | 'threads' | 'stack' | 'lens' | 'board';

interface Concept {
  id: ConceptId;
  name: string;
  href: string;
  eyebrow: string;
  title: [string, string];
  copy: string;
  accent: string;
  accentSoft: string;
  accentInk: string;
}

const concepts: Concept[] = [
  {
    id: 'canvas',
    name: 'Learning Canvas',
    href: '/identity-mockup/canvas',
    eyebrow: 'Idea 01',
    title: ['Good afternoon,', 'Nick.'],
    copy: 'Drop the material in. Formency shapes the next useful lesson from the parts that matter.',
    accent: '#f26b5e',
    accentSoft: '#fff0ec',
    accentInk: '#32120f',
  },
  {
    id: 'threads',
    name: 'Learning Threads',
    href: '/identity-mockup/threads',
    eyebrow: 'Idea 02',
    title: ['Follow the', 'thread.'],
    copy: 'A map-like identity where lessons, weak spots, images, and revision all feel connected.',
    accent: '#37b8c8',
    accentSoft: '#e9fbfd',
    accentInk: '#06292f',
  },
  {
    id: 'stack',
    name: 'Formency Stack',
    href: '/identity-mockup/stack',
    eyebrow: 'Idea 03',
    title: ['Build your', 'stack.'],
    copy: 'Source notes, lesson cards, recall prompts, and progress layers become one tactile object.',
    accent: '#d86ba3',
    accentSoft: '#fff0f7',
    accentInk: '#2f1126',
  },
  {
    id: 'lens',
    name: 'Focus Lens',
    href: '/identity-mockup/lens',
    eyebrow: 'Idea 04',
    title: ['See what', 'matters.'],
    copy: 'A calmer identity centred on focus, signal, and the best next move for today.',
    accent: '#78d6a4',
    accentSoft: '#effcf4',
    accentInk: '#0b2a1e',
  },
  {
    id: 'board',
    name: 'Study Board',
    href: '/identity-mockup/board',
    eyebrow: 'Idea 05',
    title: ['Your study', 'board.'],
    copy: 'A warmer pinned-board language with notes, image references, and soft progress badges.',
    accent: '#f5c84b',
    accentSoft: '#fff7df',
    accentInk: '#2b2205',
  },
];

const conceptMap = new Map(concepts.map((concept) => [concept.id, concept]));

function cssVars(vars: Record<string, string | number>): CSSProperties {
  return vars as CSSProperties;
}

function ConceptNav({ activeId }: { activeId: ConceptId }) {
  return (
    <nav className="identity-concept-nav" aria-label="Identity concept pages">
      {concepts.map((concept) => (
        <Link
          key={concept.id}
          to={concept.href}
          className={
            concept.id === activeId
              ? 'identity-concept-nav__link identity-concept-nav__link--active'
              : 'identity-concept-nav__link'
          }
          style={cssVars({
            '--nav-accent': concept.accent,
            '--nav-soft': concept.accentSoft,
            '--nav-ink': concept.accentInk,
          })}
        >
          {concept.name}
        </Link>
      ))}
    </nav>
  );
}

function HeroActions() {
  return (
    <div className="identity-hero-actions">
      <Link to="/revision" className="identity-primary-action">
        <Play size={16} fill="currentColor" strokeWidth={2.4} />
        Start 10 min
        <ArrowRight size={16} strokeWidth={2.4} />
      </Link>
      <button type="button" className="identity-secondary-action">
        <Paperclip size={16} strokeWidth={2.2} />
        Add notes
      </button>
    </div>
  );
}

function CanvasVisual() {
  return (
    <div className="identity-visual identity-canvas-visual" aria-label="Learning canvas visual">
      <article className="canvas-note canvas-note--source">
        <span className="visual-pill visual-pill--warm">
          <FileText size={14} strokeWidth={2.2} />
          Source note
        </span>
        <h3>Psoriasis flare factors</h3>
        <p>Triggers, first-line care, escalation signs, counselling points.</p>
        <div className="highlight-lines">
          <span />
          <span />
          <span />
        </div>
      </article>

      <div className="canvas-transform" aria-hidden="true">
        <WandSparkles size={18} strokeWidth={2.4} />
      </div>

      <article className="canvas-note canvas-note--lesson">
        <span className="visual-pill visual-pill--teal">
          <BookOpen size={14} strokeWidth={2.2} />
          Lesson ready
        </span>
        <h3>Clinical picture</h3>
        <div className="canvas-question">
          <span>Recall</span>
          <strong>Which feature suggests urgent review?</strong>
        </div>
      </article>

      <div className="canvas-image-card">
        <FileImage size={22} strokeWidth={2.1} />
        <span>image cue</span>
      </div>
    </div>
  );
}

function ThreadsVisual() {
  return (
    <div className="identity-visual identity-threads-visual" aria-label="Learning threads visual">
      <svg className="threads-lines" viewBox="0 0 560 320" aria-hidden="true">
        <path d="M92 216 C174 80 310 74 448 126" />
        <path d="M128 232 C222 248 300 230 398 188" />
        <path d="M180 106 C228 154 268 176 334 206" />
      </svg>

      <div className="thread-node thread-node--home">
        <Route size={19} strokeWidth={2.4} />
        <strong>Finals</strong>
        <span>7%</span>
      </div>
      <div className="thread-node thread-node--topic">
        <Brain size={18} strokeWidth={2.3} />
        <strong>Dermatology</strong>
        <span>12 subjects</span>
      </div>
      <div className="thread-node thread-node--weak">
        <Target size={18} strokeWidth={2.3} />
        <strong>Weak spots</strong>
        <span>5 ready</span>
      </div>
      <div className="thread-node thread-node--image">
        <FileImage size={18} strokeWidth={2.3} />
        <strong>Image recall</strong>
        <span>2 cues</span>
      </div>
      <div className="thread-node thread-node--session">
        <Play size={17} fill="currentColor" strokeWidth={2.3} />
        <strong>Next 10 min</strong>
        <span>Eczema</span>
      </div>
    </div>
  );
}

function StackVisual() {
  return (
    <div className="identity-visual identity-stack-visual" aria-label="Formency stack visual">
      <article className="stack-layer stack-layer--source">
        <span className="visual-pill">Source</span>
        <h3>Lecture notes</h3>
        <div className="stack-lines">
          <span />
          <span />
          <span />
        </div>
      </article>
      <article className="stack-layer stack-layer--lesson">
        <span className="visual-pill visual-pill--warm">Lesson</span>
        <h3>Psoriasis overview</h3>
        <p>12 checks built</p>
      </article>
      <article className="stack-layer stack-layer--recall">
        <span className="visual-pill visual-pill--teal">Recall</span>
        <h3>Differentiate plaque vs guttate</h3>
        <button type="button">Review card</button>
      </article>
      <div className="stack-progress">
        <span>Progress</span>
        <strong>66%</strong>
      </div>
    </div>
  );
}

function LensVisual() {
  return (
    <div className="identity-visual identity-lens-visual" aria-label="Focus lens visual">
      <div className="lens-orbit lens-orbit--outer" />
      <div className="lens-orbit lens-orbit--inner" />
      <article className="lens-core">
        <Search size={24} strokeWidth={2.4} />
        <span>Focus</span>
        <h3>Eczema</h3>
        <p>Build volume</p>
      </article>
      <div className="lens-signal lens-signal--accuracy">
        <strong>66%</strong>
        <span>accuracy</span>
      </div>
      <div className="lens-signal lens-signal--time">
        <strong>10</strong>
        <span>minutes</span>
      </div>
      <div className="lens-signal lens-signal--weak">
        <strong>5</strong>
        <span>responses</span>
      </div>
    </div>
  );
}

function BoardVisual() {
  return (
    <div className="identity-visual identity-board-visual" aria-label="Study board visual">
      <article className="board-pin board-pin--main">
        <span className="pin-dot" />
        <h3>Psoriasis</h3>
        <p>Ready for a short session</p>
      </article>
      <article className="board-pin board-pin--note">
        <span className="pin-dot" />
        <MessageSquareText size={18} strokeWidth={2.3} />
        <strong>Ask: escalation signs</strong>
      </article>
      <article className="board-pin board-pin--image">
        <span className="pin-dot" />
        <FileImage size={20} strokeWidth={2.2} />
        <span>image cue</span>
      </article>
      <article className="board-pin board-pin--progress">
        <span className="pin-dot" />
        <strong>47 subjects</strong>
        <div className="board-progress-bar">
          <span />
        </div>
      </article>
    </div>
  );
}

function ConceptVisual({ id }: { id: ConceptId }) {
  switch (id) {
    case 'threads':
      return <ThreadsVisual />;
    case 'stack':
      return <StackVisual />;
    case 'lens':
      return <LensVisual />;
    case 'board':
      return <BoardVisual />;
    case 'canvas':
    default:
      return <CanvasVisual />;
  }
}

function CourseRow({ concept }: { concept: Concept }) {
  return (
    <article
      className="identity-course-row"
      style={cssVars({
        '--course-accent': concept.accent,
        '--course-soft': concept.accentSoft,
        '--course-ink': concept.accentInk,
      })}
    >
      <div className="identity-course-row__cover">
        <BookOpen size={25} strokeWidth={2.25} />
        <span>7%</span>
      </div>
      <div className="identity-course-row__copy">
        <span>Course path</span>
        <h3>UK Medical School Finals</h3>
        <p>Next lesson 2 of 15</p>
        <div className="identity-course-progress">
          <span />
        </div>
      </div>
      <button type="button" className="identity-icon-button" aria-label="Open course">
        <ArrowRight size={17} strokeWidth={2.4} />
      </button>
    </article>
  );
}

function MiniCard({
  icon,
  label,
  title,
  meta,
  color,
}: {
  icon: ReactNode;
  label: string;
  title: string;
  meta: string;
  color: string;
}) {
  return (
    <article className="identity-mini-card" style={cssVars({ '--mini-color': color })}>
      <span className="identity-mini-card__icon">{icon}</span>
      <div>
        <span>{label}</span>
        <strong>{title}</strong>
        <small>{meta}</small>
      </div>
    </article>
  );
}

function SupportContent({ concept }: { concept: Concept }) {
  return (
    <div className="identity-content-grid">
      <main className="identity-study-stack" aria-label="Courses and creation">
        <section className="identity-section">
          <div className="identity-section-heading">
            <div>
              <span className="identity-kicker">In motion</span>
              <h2>Your courses</h2>
            </div>
            <Link to="/my-courses" className="identity-text-link">
              Browse all <ArrowRight size={15} strokeWidth={2.3} />
            </Link>
          </div>

          <CourseRow concept={concept} />
          <div className="identity-mini-grid">
            <MiniCard
              icon={<Layers3 size={20} strokeWidth={2.2} />}
              label="Visual recall"
              title="Diagram course"
              meta="50% complete"
              color="#37b8c8"
            />
            <MiniCard
              icon={<ClipboardList size={20} strokeWidth={2.2} />}
              label="Short loop"
              title="Test course"
              meta="3 lessons left"
              color="#d86ba3"
            />
          </div>
        </section>

        <section className="identity-create-panel" aria-labelledby="identity-create-title">
          <div className="identity-create-panel__copy">
            <span className="identity-kicker">Create</span>
            <h2 id="identity-create-title">What are we making sense of next?</h2>
            <div className="identity-chip-row">
              <button type="button">Sepsis protocols</button>
              <button type="button">Maple Syrup Urine Disease</button>
              <button type="button">Respiratory acidosis</button>
            </div>
          </div>

          <div className="identity-create-panel__actions">
            <span>
              <Timer size={15} strokeWidth={2.2} />
              10 min lesson
            </span>
            <button
              type="button"
              className="identity-primary-action identity-primary-action--small"
            >
              Create lesson
              <ArrowRight size={15} strokeWidth={2.3} />
            </button>
          </div>
        </section>
      </main>

      <aside className="identity-learning-rail" aria-label="Learning status">
        <section className="identity-week-card">
          <div className="identity-section-heading identity-section-heading--compact">
            <div>
              <span className="identity-kicker">Your week</span>
              <h2>47 subjects</h2>
            </div>
            <span className="identity-soft-pill">This week</span>
          </div>

          <div className="identity-week-bar" aria-label="Learning progress distribution">
            <span className="identity-week-bar__blue" />
            <span className="identity-week-bar__orange" />
            <span className="identity-week-bar__green" />
          </div>

          <div className="identity-stat-row">
            <span>
              <strong>1</strong>
              new lesson
            </span>
            <span>
              <strong>66%</strong>
              accuracy
            </span>
            <span>
              <strong>35 min</strong>
              studied
            </span>
          </div>
        </section>

        <section className="identity-next-card">
          <span className="identity-kicker">Next session</span>
          <div>
            <span className="identity-next-card__badge">Focus</span>
            <h2>Eczema</h2>
            <p>Only 5 responses so far. Build volume without turning it into a marathon.</p>
          </div>
          <Link to="/revision" className="identity-next-card__action">
            <Play size={16} fill="currentColor" strokeWidth={2.3} />
            Start revision
            <ArrowRight size={16} strokeWidth={2.3} />
          </Link>
        </section>

        <section className="identity-rhythm-card">
          <div className="identity-section-heading identity-section-heading--compact">
            <div>
              <span className="identity-kicker">Rhythm</span>
              <h2>Next three</h2>
            </div>
            <CheckCircle2 size={20} strokeWidth={2.3} />
          </div>
          <div className="identity-rhythm-list">
            <MiniCard
              icon={<Lightbulb size={19} strokeWidth={2.2} />}
              label="Warm-up"
              title="Psoriasis triggers"
              meta="3 questions"
              color="#f26b5e"
            />
            <MiniCard
              icon={<Brain size={19} strokeWidth={2.2} />}
              label="Focus"
              title="Eczema revision"
              meta="10 min"
              color="#37b8c8"
            />
          </div>
        </section>
      </aside>
    </div>
  );
}

export default function InvitingHomeMockup() {
  const params = useParams();
  const activeId = conceptMap.has(params.conceptId as ConceptId)
    ? (params.conceptId as ConceptId)
    : 'canvas';
  const concept = conceptMap.get(activeId) ?? concepts[0];

  return (
    <PageContainer
      width="wide"
      className="identity-home-page"
      style={{
        paddingTop: 'var(--page-top-spacing)',
        paddingBottom: 104,
        fontFamily: 'var(--font-sans)',
      }}
    >
      <ConceptNav activeId={activeId} />

      <section
        className={`identity-hero identity-hero--${concept.id}`}
        aria-labelledby="identity-canvas-title"
        style={cssVars({
          '--concept-accent': concept.accent,
          '--concept-soft': concept.accentSoft,
          '--concept-ink': concept.accentInk,
        })}
      >
        <div className="identity-hero__copy">
          <span className="identity-kicker identity-kicker--inline">
            <Sparkles size={15} strokeWidth={2.3} />
            {concept.eyebrow}
          </span>
          <h1 id="identity-canvas-title">
            <span>{concept.title[0]}</span>
            <span>{concept.title[1]}</span>
          </h1>
          <p>{concept.copy}</p>
          <HeroActions />
        </div>

        <ConceptVisual id={concept.id} />
      </section>

      <SupportContent concept={concept} />

      <style>{`
        .identity-home-page,
        .identity-home-page * {
          box-sizing: border-box;
          letter-spacing: 0;
        }

        .identity-home-page {
          color: #1d1b2a;
        }

        .identity-concept-nav {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 18px;
        }

        .identity-concept-nav__link {
          display: inline-flex;
          min-height: 38px;
          align-items: center;
          justify-content: center;
          padding: 0 14px;
          border: 1px solid rgba(25, 23, 36, 0.08);
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.72);
          color: #62596f;
          font-size: 0.86rem;
          font-weight: 800;
          text-decoration: none;
          box-shadow: inset 0 0.5px 0 rgba(255, 255, 255, 0.75);
        }

        .identity-concept-nav__link--active {
          border-color: color-mix(in srgb, var(--nav-accent) 42%, rgba(25, 23, 36, 0.08));
          background: var(--nav-soft);
          color: var(--nav-ink);
        }

        .identity-hero {
          position: relative;
          overflow: hidden;
          display: grid;
          grid-template-columns: minmax(0, 0.9fr) minmax(380px, 1fr);
          gap: clamp(24px, 4vw, 56px);
          min-height: 440px;
          align-items: center;
          padding: clamp(24px, 3vw, 38px);
          border: 1px solid rgba(25, 23, 36, 0.08);
          border-radius: 30px;
          background:
            linear-gradient(90deg, rgba(25, 23, 36, 0.035) 1px, transparent 1px),
            linear-gradient(180deg, rgba(25, 23, 36, 0.035) 1px, transparent 1px),
            linear-gradient(135deg, #fff8ee 0%, #f5fbff 48%, #f2fff7 100%);
          background-size: 28px 28px, 28px 28px, auto;
          box-shadow: 0 24px 70px rgba(48, 43, 68, 0.1);
        }

        .identity-hero__copy {
          display: grid;
          gap: 16px;
          min-width: 0;
          max-width: 690px;
        }

        .identity-kicker {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          width: fit-content;
          color: #62596f;
          font-size: 0.83rem;
          font-weight: 800;
          line-height: 1.2;
          text-transform: uppercase;
        }

        .identity-kicker--inline {
          min-height: 32px;
          padding: 0 11px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.76);
          color: color-mix(in srgb, var(--concept-accent) 76%, #1d1b2a);
          box-shadow: inset 0 0.5px 0 rgba(255, 255, 255, 0.75);
        }

        .identity-hero h1,
        .identity-section-heading h2,
        .identity-create-panel h2,
        .identity-week-card h2,
        .identity-next-card h2,
        .identity-rhythm-card h2 {
          margin: 0;
          font-family: var(--font-display);
          color: #1d1b2a;
          font-weight: 800;
          line-height: 1.06;
        }

        .identity-hero h1 {
          font-size: clamp(3rem, 5.6vw, 5.7rem);
        }

        .identity-hero h1 span {
          display: block;
        }

        .identity-hero p {
          max-width: 42rem;
          margin: 0;
          color: #5f586b;
          font-size: 1.12rem;
          font-weight: 650;
          line-height: 1.45;
        }

        .identity-hero-actions,
        .identity-create-panel__actions {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .identity-primary-action,
        .identity-secondary-action,
        .identity-text-link,
        .identity-icon-button,
        .identity-chip-row button,
        .identity-next-card__action {
          border: 0;
          font-family: var(--font-sans);
          text-decoration: none;
          cursor: pointer;
        }

        .identity-primary-action,
        .identity-next-card__action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          min-height: 50px;
          padding: 0 18px;
          border-radius: 999px;
          background: #1d1b2a;
          color: #ffffff;
          font-size: 0.95rem;
          font-weight: 800;
          box-shadow: 0 14px 28px rgba(25, 23, 36, 0.18);
          transition:
            transform 150ms ease,
            box-shadow 150ms ease;
        }

        .identity-primary-action:hover,
        .identity-next-card__action:hover {
          transform: translateY(-1px);
          box-shadow: 0 18px 36px rgba(25, 23, 36, 0.22);
        }

        .identity-primary-action--small {
          min-height: 44px;
          padding: 0 15px;
          font-size: 0.9rem;
        }

        .identity-secondary-action {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: 50px;
          padding: 0 16px;
          border: 1px solid rgba(25, 23, 36, 0.1);
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.72);
          color: #342f3f;
          font-size: 0.94rem;
          font-weight: 750;
          box-shadow: inset 0 0.5px 0 rgba(255, 255, 255, 0.8);
        }

        .identity-visual {
          position: relative;
          min-height: 348px;
          width: 100%;
        }

        .visual-pill {
          display: inline-flex;
          min-height: 28px;
          align-items: center;
          gap: 7px;
          width: fit-content;
          padding: 0 10px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.76);
          color: #62596f;
          font-size: 0.78rem;
          font-weight: 850;
        }

        .visual-pill--warm {
          background: #fff0ec;
          color: #c94f42;
        }

        .visual-pill--teal {
          background: #e9fbfd;
          color: #0f7581;
        }

        .canvas-note,
        .stack-layer,
        .thread-node,
        .lens-core,
        .lens-signal,
        .board-pin {
          border: 1px solid rgba(25, 23, 36, 0.1);
          box-shadow: 0 18px 42px rgba(42, 36, 62, 0.11);
        }

        .canvas-note {
          position: absolute;
          display: grid;
          gap: 12px;
          padding: 18px;
          border-radius: 22px;
          background: rgba(255, 255, 255, 0.86);
        }

        .canvas-note h3,
        .stack-layer h3,
        .thread-node strong,
        .lens-core h3,
        .board-pin h3,
        .identity-course-row h3,
        .identity-mini-card strong {
          margin: 0;
          color: #1d1b2a;
          font-family: var(--font-display);
          font-weight: 850;
          line-height: 1.1;
        }

        .canvas-note p,
        .stack-layer p,
        .board-pin p {
          margin: 0;
          color: #62596f;
          font-size: 0.9rem;
          font-weight: 650;
          line-height: 1.35;
        }

        .canvas-note--source {
          left: 4%;
          top: 8%;
          width: min(285px, 48%);
          transform: rotate(-3deg);
        }

        .canvas-note--lesson {
          right: 5%;
          bottom: 8%;
          width: min(300px, 50%);
          background: #fff7df;
          transform: rotate(2deg);
        }

        .highlight-lines {
          display: grid;
          gap: 8px;
        }

        .highlight-lines span {
          height: 10px;
          border-radius: 999px;
          background: #f5c84b;
        }

        .highlight-lines span:nth-child(2) {
          width: 72%;
          background: #f26b5e;
        }

        .highlight-lines span:nth-child(3) {
          width: 54%;
          background: #37b8c8;
        }

        .canvas-transform {
          position: absolute;
          left: 45%;
          top: 43%;
          display: inline-flex;
          width: 54px;
          height: 54px;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          background: #1d1b2a;
          color: #ffffff;
          box-shadow: 0 14px 34px rgba(25, 23, 36, 0.18);
        }

        .canvas-question {
          display: grid;
          gap: 6px;
          padding: 14px;
          border-radius: 16px;
          background: rgba(255, 255, 255, 0.62);
        }

        .canvas-question span {
          color: #0f7581;
          font-size: 0.76rem;
          font-weight: 850;
          text-transform: uppercase;
        }

        .canvas-question strong {
          color: #1d1b2a;
          font-size: 1rem;
          line-height: 1.25;
        }

        .canvas-image-card {
          position: absolute;
          right: 22%;
          top: 5%;
          display: grid;
          place-items: center;
          gap: 8px;
          width: 96px;
          height: 96px;
          border-radius: 22px;
          background: #e9fbfd;
          color: #0f7581;
          font-size: 0.76rem;
          font-weight: 850;
          transform: rotate(6deg);
        }

        .threads-lines {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
        }

        .threads-lines path {
          fill: none;
          stroke: color-mix(in srgb, var(--concept-accent) 52%, rgba(29, 27, 42, 0.18));
          stroke-width: 5;
          stroke-linecap: round;
        }

        .thread-node {
          position: absolute;
          display: grid;
          gap: 4px;
          min-width: 142px;
          padding: 14px;
          border-radius: 20px;
          background: rgba(255, 255, 255, 0.88);
        }

        .thread-node svg {
          color: var(--concept-accent);
        }

        .thread-node span {
          color: #62596f;
          font-size: 0.8rem;
          font-weight: 750;
        }

        .thread-node--home {
          left: 2%;
          bottom: 17%;
          background: #fff0ec;
        }

        .thread-node--topic {
          left: 20%;
          top: 9%;
        }

        .thread-node--weak {
          right: 8%;
          top: 15%;
          background: #fff7df;
        }

        .thread-node--image {
          left: 39%;
          bottom: 10%;
          background: #effcf4;
        }

        .thread-node--session {
          right: 2%;
          bottom: 20%;
          background: #1d1b2a;
          color: #ffffff;
        }

        .thread-node--session strong,
        .thread-node--session span,
        .thread-node--session svg {
          color: #ffffff;
        }

        .stack-layer {
          position: absolute;
          display: grid;
          gap: 10px;
          width: min(380px, 74%);
          padding: 20px;
          border-radius: 24px;
          background: rgba(255, 255, 255, 0.9);
        }

        .stack-layer--source {
          left: 6%;
          top: 5%;
          transform: rotate(-5deg);
        }

        .stack-layer--lesson {
          left: 18%;
          top: 26%;
          background: #fff0f7;
          transform: rotate(2deg);
        }

        .stack-layer--recall {
          left: 30%;
          top: 50%;
          background: #e9fbfd;
          transform: rotate(-2deg);
        }

        .stack-lines {
          display: grid;
          gap: 8px;
        }

        .stack-lines span {
          height: 8px;
          border-radius: 999px;
          background: rgba(29, 27, 42, 0.12);
        }

        .stack-lines span:nth-child(2) {
          width: 78%;
        }

        .stack-lines span:nth-child(3) {
          width: 56%;
        }

        .stack-layer button {
          width: fit-content;
          min-height: 36px;
          padding: 0 12px;
          border: 0;
          border-radius: 999px;
          background: #1d1b2a;
          color: #ffffff;
          font-size: 0.82rem;
          font-weight: 850;
        }

        .stack-progress {
          position: absolute;
          right: 6%;
          bottom: 6%;
          display: grid;
          place-items: center;
          gap: 4px;
          width: 118px;
          height: 118px;
          border-radius: 999px;
          background: #f5c84b;
          color: #2b2205;
          box-shadow: 0 18px 42px rgba(42, 36, 62, 0.14);
        }

        .stack-progress span {
          font-size: 0.76rem;
          font-weight: 850;
          text-transform: uppercase;
        }

        .stack-progress strong {
          font-size: 2rem;
          line-height: 1;
        }

        .lens-orbit {
          position: absolute;
          left: 50%;
          top: 50%;
          border: 1px solid color-mix(in srgb, var(--concept-accent) 42%, rgba(29, 27, 42, 0.12));
          border-radius: 999px;
          transform: translate(-50%, -50%);
        }

        .lens-orbit--outer {
          width: 330px;
          height: 330px;
        }

        .lens-orbit--inner {
          width: 226px;
          height: 226px;
        }

        .lens-core {
          position: absolute;
          left: 50%;
          top: 50%;
          display: grid;
          place-items: center;
          gap: 8px;
          width: 178px;
          height: 178px;
          border-radius: 999px;
          background: #1d1b2a;
          color: #ffffff;
          transform: translate(-50%, -50%);
        }

        .lens-core h3,
        .lens-core span,
        .lens-core p,
        .lens-core svg {
          color: #ffffff;
        }

        .lens-core span {
          font-size: 0.78rem;
          font-weight: 850;
          text-transform: uppercase;
        }

        .lens-core h3 {
          font-size: 1.75rem;
        }

        .lens-core p {
          margin: 0;
          font-size: 0.9rem;
          font-weight: 750;
          opacity: 0.72;
        }

        .lens-signal {
          position: absolute;
          display: grid;
          gap: 2px;
          min-width: 108px;
          padding: 12px 14px;
          border-radius: 18px;
          background: rgba(255, 255, 255, 0.9);
        }

        .lens-signal strong {
          color: #1d1b2a;
          font-size: 1.45rem;
          line-height: 1;
        }

        .lens-signal span {
          color: #62596f;
          font-size: 0.76rem;
          font-weight: 800;
        }

        .lens-signal--accuracy {
          left: 8%;
          top: 16%;
          background: #e9fbfd;
        }

        .lens-signal--time {
          right: 9%;
          top: 21%;
          background: #fff7df;
        }

        .lens-signal--weak {
          left: 16%;
          bottom: 14%;
          background: #fff0ec;
        }

        .board-pin {
          position: absolute;
          display: grid;
          gap: 8px;
          padding: 18px;
          border-radius: 22px;
          background: rgba(255, 255, 255, 0.9);
        }

        .pin-dot {
          position: absolute;
          left: 18px;
          top: -8px;
          width: 18px;
          height: 18px;
          border-radius: 999px;
          background: #1d1b2a;
          box-shadow: inset 0 0 0 5px var(--concept-accent);
        }

        .board-pin--main {
          left: 10%;
          top: 12%;
          width: min(310px, 52%);
          background: #fff7df;
          transform: rotate(-2deg);
        }

        .board-pin--main h3 {
          font-size: 2rem;
        }

        .board-pin--note {
          right: 8%;
          top: 9%;
          width: min(230px, 42%);
          background: #fff0ec;
          transform: rotate(3deg);
        }

        .board-pin--image {
          left: 7%;
          bottom: 11%;
          place-items: center;
          width: 142px;
          height: 122px;
          background: #e9fbfd;
          color: #0f7581;
          transform: rotate(4deg);
        }

        .board-pin--progress {
          right: 12%;
          bottom: 13%;
          width: min(260px, 48%);
          background: #effcf4;
          transform: rotate(-3deg);
        }

        .board-progress-bar {
          height: 10px;
          overflow: hidden;
          border-radius: 999px;
          background: rgba(29, 27, 42, 0.1);
        }

        .board-progress-bar span {
          display: block;
          width: 66%;
          height: 100%;
          border-radius: inherit;
          background: #78d6a4;
        }

        .identity-content-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(360px, 520px);
          gap: 26px;
          align-items: start;
          margin-top: 28px;
        }

        .identity-study-stack,
        .identity-learning-rail,
        .identity-section {
          display: grid;
          gap: 18px;
          min-width: 0;
        }

        .identity-section-heading {
          display: flex;
          align-items: end;
          justify-content: space-between;
          gap: 16px;
        }

        .identity-section-heading--compact {
          align-items: center;
        }

        .identity-section-heading h2,
        .identity-create-panel h2,
        .identity-week-card h2,
        .identity-rhythm-card h2 {
          margin-top: 6px;
          font-size: 1.65rem;
        }

        .identity-text-link {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-height: 36px;
          padding: 0 12px;
          border-radius: 999px;
          background: #e9fbfd;
          color: #0f7581;
          font-size: 0.88rem;
          font-weight: 800;
          white-space: nowrap;
        }

        .identity-course-row,
        .identity-create-panel,
        .identity-week-card,
        .identity-next-card,
        .identity-rhythm-card,
        .identity-mini-card {
          border: 1px solid rgba(25, 23, 36, 0.09);
          border-radius: 24px;
          box-shadow: 0 16px 40px rgba(42, 36, 62, 0.075);
        }

        .identity-course-row {
          display: grid;
          grid-template-columns: auto minmax(0, 1fr) auto;
          align-items: center;
          gap: 16px;
          min-height: 154px;
          padding: 17px;
          background:
            linear-gradient(90deg, color-mix(in srgb, var(--course-accent) 13%, transparent) 1px, transparent 1px),
            linear-gradient(180deg, color-mix(in srgb, var(--course-accent) 13%, transparent) 1px, transparent 1px),
            linear-gradient(115deg, var(--course-soft), rgba(255, 255, 255, 0.86));
          background-size: 22px 22px, 22px 22px, auto;
        }

        .identity-course-row__cover {
          position: relative;
          display: flex;
          width: 106px;
          height: 106px;
          align-items: center;
          justify-content: center;
          border-radius: 24px;
          background: var(--course-accent);
          color: var(--course-ink);
        }

        .identity-course-row__cover span {
          position: absolute;
          left: 9px;
          bottom: 9px;
          display: inline-flex;
          min-width: 40px;
          min-height: 28px;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.72);
          font-size: 0.82rem;
          font-weight: 850;
        }

        .identity-course-row__copy {
          display: grid;
          gap: 6px;
          min-width: 0;
        }

        .identity-course-row__copy > span,
        .identity-mini-card span,
        .identity-mini-card small {
          overflow: hidden;
          color: #62596f;
          font-size: 0.78rem;
          font-weight: 800;
          line-height: 1.2;
          text-overflow: ellipsis;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .identity-course-row h3 {
          overflow: hidden;
          font-size: 1.58rem;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .identity-course-row p {
          margin: 0;
          color: #62596f;
          font-size: 0.92rem;
          font-weight: 750;
        }

        .identity-course-progress {
          height: 8px;
          overflow: hidden;
          border-radius: 999px;
          background: rgba(29, 27, 42, 0.08);
        }

        .identity-course-progress span {
          display: block;
          width: 7%;
          height: 100%;
          border-radius: inherit;
          background: var(--course-accent);
        }

        .identity-icon-button {
          display: inline-flex;
          width: 40px;
          height: 40px;
          flex-shrink: 0;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.76);
          color: #342f3f;
        }

        .identity-mini-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }

        .identity-mini-card {
          display: grid;
          grid-template-columns: auto minmax(0, 1fr);
          gap: 12px;
          align-items: center;
          min-height: 96px;
          padding: 14px;
          background: color-mix(in srgb, var(--mini-color) 14%, #ffffff);
        }

        .identity-mini-card__icon {
          display: inline-flex;
          width: 48px;
          height: 48px;
          align-items: center;
          justify-content: center;
          border-radius: 16px;
          background: var(--mini-color);
          color: #1d1b2a;
        }

        .identity-mini-card div {
          display: grid;
          gap: 4px;
          min-width: 0;
        }

        .identity-mini-card strong {
          overflow: hidden;
          font-size: 1rem;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .identity-mini-card small {
          text-transform: none;
        }

        .identity-create-panel {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 20px;
          align-items: end;
          padding: 22px;
          background: #ffffff;
        }

        .identity-create-panel__copy {
          display: grid;
          gap: 12px;
          min-width: 0;
        }

        .identity-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .identity-chip-row button {
          min-height: 34px;
          max-width: 100%;
          padding: 0 12px;
          border: 1px solid rgba(25, 23, 36, 0.09);
          border-radius: 999px;
          background: #fff7e6;
          color: #5b513e;
          font-size: 0.87rem;
          font-weight: 750;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .identity-create-panel__actions {
          justify-content: flex-end;
        }

        .identity-create-panel__actions > span {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          min-height: 36px;
          color: #62596f;
          font-size: 0.88rem;
          font-weight: 750;
          white-space: nowrap;
        }

        .identity-week-card,
        .identity-rhythm-card {
          display: grid;
          gap: 18px;
          padding: 22px;
          background: rgba(255, 255, 255, 0.82);
        }

        .identity-soft-pill {
          display: inline-flex;
          min-height: 32px;
          align-items: center;
          padding: 0 10px;
          border-radius: 999px;
          background: #fff0ec;
          color: #c94f42;
          font-size: 0.8rem;
          font-weight: 800;
          white-space: nowrap;
        }

        .identity-week-bar {
          display: grid;
          grid-template-columns: 58fr 26fr 16fr;
          height: 18px;
          overflow: hidden;
          border-radius: 999px;
          background: #edf0f3;
        }

        .identity-week-bar span + span {
          border-left: 2px solid rgba(255, 255, 255, 0.8);
        }

        .identity-week-bar__blue {
          background: #7bb7d9;
        }

        .identity-week-bar__orange {
          background: #f0ad4e;
        }

        .identity-week-bar__green {
          background: #78d6a4;
        }

        .identity-stat-row {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
        }

        .identity-stat-row span {
          display: grid;
          gap: 3px;
          min-width: 0;
          color: #6a6374;
          font-size: 0.78rem;
          font-weight: 700;
          line-height: 1.2;
        }

        .identity-stat-row strong {
          overflow: hidden;
          color: #1d1b2a;
          font-size: 1.02rem;
          font-weight: 850;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .identity-next-card {
          display: grid;
          gap: 18px;
          padding: 24px;
          background:
            linear-gradient(90deg, rgba(255, 255, 255, 0.055) 1px, transparent 1px),
            linear-gradient(180deg, rgba(255, 255, 255, 0.055) 1px, transparent 1px),
            #171522;
          background-size: 26px 26px, 26px 26px, auto;
          color: #ffffff;
          box-shadow: 0 22px 52px rgba(25, 23, 36, 0.2);
        }

        .identity-next-card .identity-kicker {
          color: #78d6a4;
        }

        .identity-next-card__badge {
          display: inline-flex;
          min-height: 31px;
          align-items: center;
          padding: 0 10px;
          border-radius: 999px;
          background: #37b8c8;
          color: #06292f;
          font-size: 0.76rem;
          font-weight: 850;
          text-transform: uppercase;
        }

        .identity-next-card h2 {
          margin-top: 12px;
          color: #ffffff;
          font-size: 2rem;
        }

        .identity-next-card p {
          margin: 8px 0 0;
          color: rgba(255, 255, 255, 0.72);
          font-size: 0.95rem;
          font-weight: 650;
          line-height: 1.45;
        }

        .identity-next-card__action {
          width: 100%;
          background: #f5c84b;
          color: #2b2205;
          box-shadow: none;
        }

        .identity-rhythm-card .identity-section-heading svg {
          color: #78d6a4;
        }

        .identity-rhythm-list {
          display: grid;
          gap: 10px;
        }

        @media (max-width: 1180px) {
          .identity-hero,
          .identity-content-grid {
            grid-template-columns: 1fr;
          }

          .identity-visual {
            min-height: 330px;
          }
        }

        @media (max-width: 760px) {
          .identity-hero {
            min-height: 0;
            padding: 20px;
            border-radius: 24px;
          }

          .identity-hero h1 {
            font-size: 3rem;
          }

          .identity-hero p {
            font-size: 1.02rem;
          }

          .identity-visual {
            min-height: 410px;
          }

          .canvas-note--source,
          .canvas-note--lesson,
          .board-pin--main,
          .board-pin--note,
          .board-pin--progress {
            width: min(100%, 280px);
          }

          .thread-node {
            min-width: 126px;
          }

          .identity-course-row,
          .identity-create-panel,
          .identity-mini-grid,
          .identity-stat-row {
            grid-template-columns: 1fr;
          }

          .identity-icon-button {
            justify-self: start;
          }

          .identity-create-panel__actions {
            justify-content: flex-start;
          }
        }

        @media (max-width: 520px) {
          .identity-hero {
            padding: 16px;
          }

          .identity-hero h1 {
            font-size: 2.45rem;
          }

          .identity-primary-action,
          .identity-secondary-action {
            width: 100%;
          }

          .identity-visual {
            min-height: 460px;
          }

          .identity-course-row__cover {
            width: 82px;
            height: 82px;
            border-radius: 20px;
          }

          .identity-course-row h3 {
            font-size: 1.34rem;
          }
        }
      `}</style>
    </PageContainer>
  );
}
